import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import cx from 'classnames';
import { useMounted } from '../../../hooks/useMounted';
import { useNCTextLocalizer } from '../../../locale/useNCTextLocalizer';
import { postfixTestId } from '../../../util/testId';
import { Input } from '../../Input';
import cn from './SliderValueGrip.css.json';
import './SliderValueGrip.css';
import { createUseHtmlId } from '../../../hooks/useHtmlId';
import { InvisibleText } from '../../InvisibleText';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useHtmlId = createUseHtmlId('sliderGripInput');
export const SliderInput = /*#__PURE__*/forwardRef(function SliderInput(props, ref) {
  const {
    getSliderClientRect,
    setHasSliderInputErrorOrFocus
  } = props;
  const mounted = useMounted();
  const localize = useNCTextLocalizer('Slider');
  const refInput = useRef(null);
  useImperativeHandle(ref, () => ({
    focusInput() {
      if (refInput.current) refInput.current.focus();
    },
    getInputWidth() {
      return refInput.current?.getBoundingClientRect().width;
    }
  }), [refInput]);
  const refInitialEditValue = useRef(undefined);
  if (refInitialEditValue.current === undefined) {
    refInitialEditValue.current = {
      inputValue: props.format.formatEditText ? props.format.formatEditText(props.value) : props.defaultFormat.format(props.value),
      statusMsg: null
    };
  }
  const ignoreNextCommitRef = useRef(false);
  const [{
    inputValue,
    statusMsg
  }, setInputState] = useState(refInitialEditValue.current);

  // derived state: props.value => editValue
  {
    const [prevValue, setPrevValue] = useState(props.value);
    if (prevValue !== props.value) {
      setPrevValue(props.value);
      const nextEditValue = props.format.formatEditText ? props.format.formatEditText(props.value) : props.defaultFormat.format(props.value);
      // FIXME: we could probably validate this a bit
      setInputState({
        inputValue: nextEditValue,
        statusMsg: null
      });
    }
  }
  function validateUserInput(inputText) {
    const parseResult = props.format.parse(inputText, localize, props.step);
    if (parseResult.type !== 'error') {
      if (parseResult.result < props.minValue) {
        return {
          type: 'error',
          result: localize('input.invalid.lessThanMinimum.msg', {
            minVal: props.format.format(props.minValue)
          })
        };
      } else if (parseResult.result > props.maxValue) {
        return {
          type: 'error',
          result: localize('input.invalid.greaterThanMaximum.msg', {
            maxVal: props.format.format(props.maxValue)
          })
        };
      }
    }
    return parseResult;
  }
  function onCancel() {
    if (!mounted.current) return;
    // Pass it on so the container can do whatever it needs to do.  Set a flag that can be used to avoid committing the value.
    ignoreNextCommitRef.current = true;
    // restore inputValue to prev formatted value
    const formattedValue = props.format.formatEditText ? props.format.formatEditText(props.value) : props.defaultFormat.format(props.value);
    setInputState({
      inputValue: formattedValue,
      statusMsg: null
    });
    props.onCancel();
  }
  function onCommit(value) {
    if (!mounted.current) return;
    // Abort the commit if the user pressed the escape key.
    if (ignoreNextCommitRef.current) {
      // Reset flag.
      ignoreNextCommitRef.current = false;
      return;
    }
    const validationResult = validateUserInput(value);
    let formattedValue;

    // To prevent a value commit event when the user submits a value that is equal to the current value
    if (validationResult.result === props.value) return;
    if (validationResult.type === 'error') {
      formattedValue = value;
    } else if (props.format.formatEditText) {
      formattedValue = props.format.formatEditText(props.value);
    } else {
      formattedValue = props.defaultFormat.format(props.value);
    }
    setInputState({
      inputValue: formattedValue,
      statusMsg: validationResult.type === 'error' ? validationResult.result : null
    });
    if (validationResult.type === 'success') {
      props.onCommit({
        value: validationResult.result
      });
    }
  }
  const {
    isFocused,
    onFocus,
    onBlur
  } = useIsFocused();
  const [overflowingClass, setOverflowingClass] = useState('');
  useEffect(() => {
    if (isFocused) {
      const inputRect = refInput.current?.getBoundingClientRect();
      const sliderRect = getSliderClientRect();
      if (inputRect && sliderRect) {
        const isOverflowingRight = inputRect.x + inputRect.width > sliderRect.x + sliderRect.width;
        const isOverflowingLeft = inputRect.x < sliderRect.x;
        if (isOverflowingLeft) {
          setOverflowingClass('inputOverflowLeft');
        } else if (isOverflowingRight) {
          setOverflowingClass('inputOverflowRight');
        } else {
          setOverflowingClass('');
        }
      }
    } else {
      setOverflowingClass('');
    }
  }, [isFocused, getSliderClientRect]);
  useEffect(() => {
    if (isFocused || statusMsg) {
      setHasSliderInputErrorOrFocus?.(true);
    } else {
      setHasSliderInputErrorOrFocus?.(false);
    }
  }, [isFocused, setHasSliderInputErrorOrFocus, statusMsg]);
  const htmlId = useHtmlId();
  const invisibleTextId = props.ariaLabel ? htmlId : undefined;
  const ariaLabelledBy = props.ariaLabelledBy && invisibleTextId ? `${props.ariaLabelledBy} ${invisibleTextId}` : props.ariaLabelledBy ?? invisibleTextId;
  return /*#__PURE__*/_jsxs("div", {
    "data-testid": postfixTestId(props.testId, 'inputContainer'),
    children: [props.ariaLabel && /*#__PURE__*/_jsx(InvisibleText, {
      id: invisibleTextId,
      testId: postfixTestId(props.testId, 'inputInvisibleText'),
      children: props.ariaLabel
    }), /*#__PURE__*/_jsx(Input, {
      testId: postfixTestId(props.testId, 'input'),
      className: cx(cn.input, !isFocused && statusMsg === null && cn.offscreen, overflowingClass && props.orientation === 'horizontal' && cn[overflowingClass]),
      value: inputValue,
      ref: refInput,
      commitOnEnterInDialog: true,
      onChange: e => {
        setInputState({
          inputValue: e.value,
          statusMsg: null
        });
      },
      onCommit: e => onCommit(e.value),
      onFocus: onFocus,
      onBlur: onBlur,
      onCancel: onCancel,
      status: statusMsg !== null ? 'alert' : null,
      message: statusMsg,
      style: props.style,
      ariaLabelledBy: ariaLabelledBy,
      type: "number"
    })]
  });
});
function useIsFocused() {
  const refOurFocusedElement = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  return {
    isFocused,
    onFocus: () => {
      refOurFocusedElement.current = document.activeElement;
      setIsFocused(true);
    },
    onBlur: () => {
      // NOTE: document.activeElement is usually document.body during a blur event. However,
      // it will be equal to the previous focused element when a user switches away from the browser window or tab.
      // This works on Chrome,FF,Safari,EdgeChrome. If it does break, consider using the Page Visibility API.
      if (refOurFocusedElement.current !== document.activeElement) {
        refOurFocusedElement.current = null;
        setIsFocused(false);
      }
    }
  };
}
//# sourceMappingURL=SliderInput.js.map